import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../components/common/Button';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { EditUserModal } from '../../components/admin/EditUserModal';
import { api } from '../../lib/api';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import type { UserInfo } from '../../types';

const statusColors: Record<UserInfo['status'], string> = {
  active: 'bg-green-500/20 text-green-400',
  deactivated: 'bg-red-500/20 text-red-400',
};

const roleLabels: Record<UserInfo['role'], string> = {
  admin: 'Admin',
  'framework-manager': 'Framework Manager',
  reviewer: 'Reviewer',
  'basic-user': 'User',
};

export function UserManagementPage() {
  const [editingUser, setEditingUser] = useState<UserInfo | null>(null);
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { user: currentUser } = useAuth();

  const { data: users, isLoading, error } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: api.listUsers,
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      api.updateUserRole(userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      showToast('Role updated successfully', 'success');
    },
    onError: (err) => {
      showToast(err instanceof Error ? err.message : 'Failed to update role', 'error');
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner message="Loading users..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-400 p-4">Failed to load users</div>
    );
  }

  return (
    <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-white">User Management</h1>
        </div>

        <div className="bg-wolf-800 rounded-lg border border-wolf-700 overflow-hidden">
          <table className="w-full">
            <thead className="bg-wolf-900">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-wolf-300">Email</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-wolf-300">Name</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-wolf-300">Role</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-wolf-300">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-wolf-300">Last Login</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-wolf-300">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-wolf-700">
              {users?.map((user) => (
                <tr key={user.email} className="hover:bg-wolf-700/50">
                  <td className="px-4 py-3 text-white">{user.email}</td>
                  <td className="px-4 py-3 text-wolf-300">{user.name}</td>
                  <td className="px-4 py-3">
                    <span className="text-wolf-300">{roleLabels[user.role] || user.role}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs ${statusColors[user.status]}`}>
                      {user.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-wolf-400 text-sm">
                    {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                  </td>
                  <td className="px-4 py-3">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setEditingUser(user)}
                    >
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
              {users?.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-wolf-400">
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {editingUser && (
          <EditUserModal
            key={editingUser.email}
            user={editingUser}
            currentUserId={currentUser?.email || ''}
            onClose={() => setEditingUser(null)}
            onSave={(userId, role) => updateRoleMutation.mutateAsync({ userId, role })}
            isLoading={updateRoleMutation.isPending}
          />
        )}
      </div>
  );
}
